import { PageLayout } from '@/layouts/page-layout'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Table,
  Header,
  HeaderRow,
  HeaderCell,
  Body,
  Row,
  Cell
} from '@table-library/react-table-library/table'
import { useTheme } from '@table-library/react-table-library/theme'
import { Button, Tooltip } from '@material-tailwind/react'
import { FiMenu, FiTrash2, FiUpload } from 'react-icons/fi'
import CustomSelect from '@/shared/components/custom-select'
import { monthOptions, yearOptions } from '@/components/attachment-uploader/utils'
import { fileToAttachment } from '@/utils/file-to-attachment'
import {
  attachmentTypeApi,
  importApi,
  useGetAssetCategoriesQuery,
  useGetAssetTypesQuery
} from '@/store/services'
import { store } from '@/store'
import { NotificationModal } from '@/components/notification-modal'
import { downloadMassiveImportReport } from '@/utils/massive-import-report'

export const AssetV2MassiveImport = () => {
  const { t } = useTranslation()
  const theme = useTheme({
    Table: `
      --data-table-library_grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
      table-layout: fixed;
    `,
    Cell: `
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100px !important;
    `
  })

  const initialValue = {
    id: Date.now(),
    attachment_type: {},
    period_month: '',
    period_year: '',
    files: []
  }

  const [rows, setRows] = useState([initialValue])
  const [attachmentTypes, setAttachmentTypes] = useState([])
  const inputRefs = useRef([])
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [iconTypeNotificacionModal, setIconTypeNotificacionModal] = useState('')
  const [titleNotificacionModal, setTitleNotificationModal] = useState('')
  const [messageNotificacionModal, setMessageNotificationModal] = useState('')
  const years = useMemo(() => yearOptions(), [])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedType, setSelectedType] = useState(null)

  const { data: categoriesData = {} } = useGetAssetCategoriesQuery()
  const categories = (categoriesData.asset_categories || []).map((c) => ({
    value: c.id,
    label: c.name
  }))

  const { data: typesData = {}, isFetching: loadingTypes } = useGetAssetTypesQuery(
    selectedCategory?.value,
    { skip: !selectedCategory }
  )
  const types = useMemo(() => {
    const base = (typesData.asset_types || []).map((t) => ({
      value: t.id,
      label: t.name
    }))
    return selectedCategory
      ? [{ value: null, label: '<Todos>' }, ...base]
      : base
  }, [typesData, selectedCategory])

  const addRow = () => {
    setRows((prevRows) => [...prevRows, { ...initialValue, id: Date.now() }])
  }

  const removeRow = (id) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== id))
  }

  useEffect(() => {
    const fetchAttachmentTypes = async () => {
      if (!selectedCategory) {
        setAttachmentTypes([])
        return
      }
      try {
        const typeParam = selectedType?.value ? `&asset_type_id=${selectedType.value}` : ''
        const query = `?page=1&per_page=9999&klass=Asset&asset_category_id=${selectedCategory.value}${typeParam}`
        const { attachmentTypes } = await store
          .dispatch(
            attachmentTypeApi.endpoints.getAttachmentTypes.initiate(query)
          )
          .unwrap()

        setAttachmentTypes(
          attachmentTypes.map((option) => ({
            value: option.id,
            label: option.name,
            id: option.id,
            name: option.name,
            recurrence: option.recurrence
          }))
        )
      } catch (error) {
        console.error('Error fetching attachment types:', error)
      }
    }

    fetchAttachmentTypes()
  }, [selectedCategory, selectedType])

  const handleInputChange = (id, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, [field]: value }
          : row
      )
    )
  }

  const handleFileChange = (event, id) => {
    const files = Array.from(event.target.files)
    Promise.all(
      files.map((file) =>
        new Promise((resolve) => {
          fileToAttachment(file, resolve)
        })
      )
    ).then((attachments) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === id ? { ...row, files: attachments } : row
        )
      )
    })
  }

  const handleClick = (index) => {
    inputRefs.current[index].click()
  }

  const validateRows = () => {
    if (!selectedCategory) {
      alert('Selecciona categoría de activo antes de cargar documentos.')
      return false
    }

    for (const row of rows) {
      if (!row.attachment_type.value) {
        alert('Por favor selecciona un Tipo de documento en todas las filas.')
        return false
      }

      if (
        ['monthly'].includes(row.attachment_type.recurrence) &&
        !row.period_month
      ) {
        alert('Por favor selecciona un mes en todas las filas requeridas.')
        return false
      }

      if (
        ['monthly', 'yearly'].includes(row.attachment_type.recurrence) &&
        !row.period_year
      ) {
        alert('Por favor selecciona un año en todas las filas requeridas.')
        return false
      }

      if (row.files.length === 0) {
        alert('Por favor sube al menos un archivo en cada fila.')
        return false
      }
    }
    return true
  }

  const uploadRows = async () => {
    if (!validateRows()) {
      return
    }
    try {
      const { type, body } = {
        type: 'assets_v2',
        body: {
          data: rows,
          asset_type_id: selectedType?.value || null,
          asset_category_id: selectedCategory.value
        }
      }
      const response = await store.dispatch(
        importApi.endpoints.sendAttachmentFile.initiate({ type, body })
      ).unwrap()

      if (response.status_upload === 200) {
        setRows([initialValue])
        setIconTypeNotificacionModal('check')
        setTitleNotificationModal('Importación realizada exitosamente.')
        setMessageNotificationModal('Se importaron correctamente todos los archivos')
      } else {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('Importación realizada con errores')
        setMessageNotificationModal(
          'Verifica el archivo de descarga con el reporte de la importación.'
        )
        downloadMassiveImportReport(response.errors)
      }
      setShowNotificationModal(true)
    } catch (error) {
      if (error && error.originalStatus === 413) {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('No se pudo realizar la importación')
        setMessageNotificationModal(
          'Se superó el tamaño máximo de carga.'
        )
      } else {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('No se pudo realizar la importación')
        setMessageNotificationModal(
          'Ha ocurrido un error con el servidor. Vuelve a intentarlo.'
        )
      }
      setShowNotificationModal(true)
    }
  }

  return (
    <PageLayout title={t('asset.attachmentImport')}>
      {showNotificationModal && (
        <NotificationModal
          title={titleNotificacionModal}
          message={messageNotificacionModal}
          iconType={iconTypeNotificacionModal}
          setBackStateFunction={setShowNotificationModal}
        ></NotificationModal>
      )}
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-3xl'>
        <div>
          <p className='text-sm font-medium text-gray-700 mb-1'>Categoría</p>
          <CustomSelect
            options={categories}
            value={selectedCategory}
            onChange={(opt) => {
              setSelectedCategory(opt)
              setSelectedType(null)
              setAttachmentTypes([])
            }}
            placeholder='Selecciona categoría'
          />
        </div>
        <div>
          <p className='text-sm font-medium text-gray-700 mb-1'>Tipo</p>
          <CustomSelect
            options={types}
            value={selectedType}
            onChange={(opt) => {
              setSelectedType(opt)
              setAttachmentTypes([])
            }}
            placeholder='Selecciona tipo'
            isDisabled={!selectedCategory}
            isLoading={loadingTypes}
            isClearable
          />
        </div>
      </div>
      <Table
        data={{ nodes: rows }}
        layout={{ custom: true, fixedHeader: true }}
        theme={theme}
        className='h-max'
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell>Tipo</HeaderCell>
                <HeaderCell>Mes</HeaderCell>
                <HeaderCell>Año</HeaderCell>
                <HeaderCell>Archivos</HeaderCell>
                <HeaderCell>Acciones</HeaderCell>
              </HeaderRow>
            </Header>

            <Body>
              {tableList.map((column, index) => (
                <Row key={column.id} item={column}>
                  <div>
                    <Cell>
                      <CustomSelect
                        options={attachmentTypes}
                        value={attachmentTypes.find(
                          (option) =>
                            option.value === column.attachment_type.value
                        )}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            column.id,
                            'attachment_type',
                            selectedOption || {}
                          )
                        }
                        placeholder='Seleccionar'
                      />
                    </Cell>
                  </div>
                  <Cell>
                    <CustomSelect
                      options={monthOptions}
                      value={monthOptions.find(
                        (option) => option.value === column.period_month
                      )}
                      onChange={(selectedOption) =>
                        handleInputChange(
                          column.id,
                          'period_month',
                          selectedOption ? selectedOption.value : ''
                        )
                      }
                      isDisabled={
                        !['monthly'].includes(column.attachment_type?.recurrence)
                      }
                    />
                  </Cell>
                  <Cell>
                    <CustomSelect
                      options={years}
                      value={years.find(
                        (option) => option.value === column.period_year
                      )}
                      onChange={(selectedOption) =>
                        handleInputChange(
                          column.id,
                          'period_year',
                          selectedOption ? selectedOption.value : ''
                        )
                      }
                      isDisabled={
                        !['monthly', 'yearly'].includes(
                          column.attachment_type?.recurrence
                        )
                      }
                    />
                  </Cell>
                  <Cell>
                    <div className='text-center'>
                      <input
                        type='file'
                        multiple
                        accept='application/pdf,image/*'
                        className='hidden'
                        ref={(el) => (inputRefs.current[index] = el)}
                        onChange={(event) => handleFileChange(event, column.id)}
                      />
                      <Tooltip
                        content='Seleccionar archivos'
                        placement='top'
                        className='bg-blue-500'
                      >
                        <Button
                          variant='text'
                          className='p-2 rounded-full bg-blue-50'
                          onClick={() => handleClick(index)}
                        >
                          <FiUpload size={20} color='#1976d2' />
                        </Button>
                      </Tooltip>
                      <div className='mt-2'>
                        {column.files?.length || 0} archivos seleccionados
                      </div>
                    </div>
                  </Cell>
                  <Cell>
                    <div className='flex gap-3'>
                      <Tooltip
                        content='Clonar fila'
                        placement='top'
                        className='bg-blue-500'
                      >
                        <Button
                          variant='text'
                          className='p-2 rounded-full bg-blue-50'
                          onClick={addRow}
                        >
                          <FiMenu size={20} color='#1976d2' />
                        </Button>
                      </Tooltip>
                      <Tooltip
                        content='Eliminar fila'
                        placement='top'
                        className='bg-red-500'
                      >
                        <Button
                          variant='text'
                          className='p-2 rounded-full bg-red-50'
                          onClick={() => removeRow(column.id)}
                          disabled={tableList.length === 1}
                        >
                          <FiTrash2 size={20} color='#ef4444' />
                        </Button>
                      </Tooltip>
                    </div>
                  </Cell>
                </Row>
              ))}
            </Body>
          </>
        )}
      </Table>
      <div className='flex gap-4 mt-6'>
        <Button variant='text' onClick={addRow} color='blue'>
          Agregar fila
        </Button>
        <Button color='blue' onClick={uploadRows}>
          Subir archivos
        </Button>
      </div>
      <p className='text-sm text-gray-600 mt-4'>
        Recuerda que el nombre de cada archivo debe ser solo el identificador del activo para asociarlo correctamente. Ej: BFPR76.pdf
      </p>
    </PageLayout>
  )
}
